// Sprint fc-conversation-search (§2) — in-conversation search row. Mounted
// inside `ChatView`'s live return path only (§2.2), between `ChatHeader` /
// `PlanModeBanner` and the message timeline. Results list + jump-to-message,
// never in-place highlighting inside the timeline (§10.4 — editing the
// message renderers is out of scope and would clobber on the next
// `ConversationList` render).
//
// Render order, LOCKED (§2.4) — the first state that holds wins:
//   1. stillLoading  — ChatView's `loading && messages === null`
//   2. loadFailed    — ChatView's `loadError && messages === null`
//   3. loaded, corpus non-empty results — normal results
//   4. loaded, no match — the honest zero-match copy
// Both predicates are ChatView's own ternary conditions, passed down as
// declared props — never re-derived here (R1 B5).
//
// No fetch, no store read, no new writer (§5 SSoT audit) — `messages` is
// handed down from ChatView's existing selector; the one existing writer
// this sprint calls is `usePaginatedHistory(agentId).loadOlder` (§4 row 2).

import { useEffect, useMemo, useRef, useState } from 'react';
import type { Message } from '../../../types';
import { useTranslation } from '../../../i18n';
import { usePaginatedHistory } from '../../../hooks/usePaginatedHistory';
// DS.8 (D9 + D3's byte-identity rule) — `ui/input.tsx` and `ui/button.tsx`
// retire on this, their last importer. Both take D3's DECLARED FALLBACK
// branch (product block on roles with today's markup) rather than
// `@ds/navigation/SearchBar` / `@ds/actions/IconButton`, and the reason is
// measured, not preferred: `conversation-search-gallery` is an in-frame
// baseline and D13's accepted-diff list is EMPTY — this sprint has no
// regeneration step at all, so a moved pixel is a defect. The DS `SearchBar`
// owns its own leading icon, its own expand affordance and its own box
// metrics, and `IconButton` sizes itself from a CSS-module class that ties
// specificity with the `h-7 w-7` the call sites already carry. Neither can
// be held to zero drift by construction, and D3's rule is that the swap is
// OFF for such an element. The retirement is unaffected: a role-styled
// `<input>`/`<button>` needs neither shadcn file.
//
// The four constants below are `ui/{input,button}.tsx`'s class blocks
// verbatim, composed through the same `cn()` (tailwind-merge) the cva
// wrapper used, so every rendered class string is byte-for-byte what it was.
import { cn } from '../../../lib/utils';
import { Icon } from '../../atoms/Icon';
import { formatRelativeTime } from '../../../lib/time';
import { searchMessages, SEARCH_RESULTS_CAP, type SearchMatch } from './search-messages';
import { jumpToMessage } from '../../../lib/jump-to-message';

export interface ConversationSearchProps {
  agentId: string;
  agentName: string;
  messages: Message[] | null;
  /** ChatView's `loading && messages === null` — the `chat-loading` branch. */
  stillLoading: boolean;
  /** ChatView's `loadError && messages === null` — the `chat-load-error` branch. */
  loadFailed: boolean;
  onClose: () => void;
}

function highlightedSnippet(match: SearchMatch): { before: string; hit: string; after: string } {
  if (match.matchStart < 0) {
    return { before: match.snippet, hit: '', after: '' };
  }
  const end = match.matchStart + match.matchLength;
  return {
    before: match.snippet.slice(0, match.matchStart),
    hit: match.snippet.slice(match.matchStart, end),
    after: match.snippet.slice(end),
  };
}

/** `ui/input.tsx`'s class block, verbatim (D9). */
const INPUT_CLASSES =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50';

/** `ui/button.tsx`'s cva base, verbatim (D9). */
const BTN_BASE =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0';
/** variant="ghost" · variant="outline" · size="icon" · size="sm", verbatim. */
const BTN_GHOST = 'hover:bg-accent hover:text-accent-foreground';
const BTN_OUTLINE =
  'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground';
const BTN_ICON = 'h-9 w-9';
const BTN_SM = 'h-8 rounded-md px-3 text-xs';

export function ConversationSearch({
  agentId,
  agentName,
  messages,
  stillLoading,
  loadFailed,
  onClose,
}: ConversationSearchProps) {
  const { t } = useTranslation();
  const { hasMore, loadingOlder, loadOlder } = usePaginatedHistory(agentId);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [loadOlderFailed, setLoadOlderFailed] = useState(false);
  const rowRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // EC-4 — the search's own state is component-local and per conversation;
  // it does not survive an agent switch. ConversationSearch fully unmounts
  // when ChatView closes the row on switch (its own effect), so this only
  // guards the (rarer) case of the row staying mounted across a re-render
  // with a new agentId — belt-and-suspenders, cheap and correct either way.
  useEffect(() => {
    setQuery('');
    setActiveIndex(0);
    setLoadOlderFailed(false);
  }, [agentId]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const disabled = stillLoading || loadFailed;

  const outcome = useMemo(
    () => searchMessages(disabled ? [] : (messages ?? []), query),
    [disabled, messages, query],
  );

  const total = outcome.matches.length;
  const visibleMatches = outcome.matches.slice(0, SEARCH_RESULTS_CAP);
  const hiddenCount = total - visibleMatches.length;

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  function jumpTo(id: string) {
    jumpToMessage(rowRef.current?.closest('[data-testid="chat-view"]') ?? null, id);
  }

  function selectIndex(i: number) {
    if (total === 0) return;
    const next = ((i % total) + total) % total;
    setActiveIndex(next);
    const m = outcome.matches[next];
    if (m) jumpTo(m.id);
  }

  async function handleLoadOlder() {
    setLoadOlderFailed(false);
    try {
      await loadOlder();
    } catch {
      // Sprint 15's own `loadOlderMessages` clears `loadingOlder` and says
      // nothing on failure (anchor: its `catch` branch) — silence here
      // would be a NEW silent failure, not an inherited one (EC-9).
      setLoadOlderFailed(true);
    }
  }

  return (
    <div
      ref={rowRef}
      className="shrink-0 border-b border-border bg-background px-3 py-2 flex flex-col gap-1.5"
      data-testid="conversation-search"
    >
      <div className="flex items-center gap-2">
        <Icon name="Search" size={14} className="text-muted-foreground/70 shrink-0" aria-hidden="true" />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              onClose();
            } else if (e.key === 'Enter') {
              e.preventDefault();
              selectIndex(e.shiftKey ? activeIndex - 1 : activeIndex + 1);
            }
          }}
          disabled={disabled}
          placeholder={t('conversationSearch.trigger')}
          aria-label={t('conversationSearch.trigger')}
          className={cn(INPUT_CLASSES, 'h-8 flex-1 min-w-0 bg-card border-border text-sm')}
          data-testid="conversation-search-input"
        />

        {!disabled && !outcome.queryTooShort && total > 0 && (
          <>
            <span
              className="text-caption text-muted-foreground shrink-0 tabular-nums"
              data-testid="conversation-search-count"
            >
              {t('conversationSearch.matchCount', {
                current: activeIndex + 1,
                total,
              })}
            </span>
            <button
              type="button"
              onClick={() => selectIndex(activeIndex - 1)}
              aria-label={t('conversationSearch.previous')}
              data-testid="conversation-search-prev"
              className={cn(BTN_BASE, BTN_GHOST, BTN_ICON, 'h-7 w-7 text-muted-foreground shrink-0')}
            >
              <Icon name="ArrowUp" size={14} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => selectIndex(activeIndex + 1)}
              aria-label={t('conversationSearch.next')}
              data-testid="conversation-search-next"
              className={cn(BTN_BASE, BTN_GHOST, BTN_ICON, 'h-7 w-7 text-muted-foreground shrink-0')}
            >
              <Icon name="ArrowDown" size={14} aria-hidden="true" />
            </button>
          </>
        )}

        <button
          type="button"
          onClick={onClose}
          aria-label={t('conversationSearch.close')}
          data-testid="conversation-search-close"
          className={cn(BTN_BASE, BTN_GHOST, BTN_ICON, 'h-7 w-7 text-muted-foreground shrink-0')}
        >
          <Icon name="X" size={14} aria-hidden="true" />
        </button>
      </div>

      {stillLoading ? (
        <p className="text-caption text-muted-foreground" data-testid="conversation-search-loading">
          {t('conversationSearch.loading')}
        </p>
      ) : loadFailed ? (
        <p className="text-caption text-status-error" data-testid="conversation-search-load-error">
          {t('conversationSearch.loadError')}
        </p>
      ) : (
        <>
          <p className="text-caption text-muted-foreground/70" data-testid="conversation-search-scope-hint">
            {t('conversationSearch.scopeHint')}
          </p>

          {hasMore && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="conversation-search-partial-window">
              <p className="text-caption text-muted-foreground/70">
                {t('conversationSearch.scopePartial', { count: outcome.corpusSize })}
              </p>
              <button
                type="button"
                onClick={() => void handleLoadOlder()}
                disabled={loadingOlder}
                data-testid="conversation-search-load-older"
                className={cn(BTN_BASE, BTN_OUTLINE, BTN_SM, 'h-7 text-caption shrink-0')}
              >
                {loadingOlder ? (
                  <Icon name="Loader2" size={12} className="animate-spin" aria-hidden="true" />
                ) : null}
                {t('conversationSearch.loadOlder')}
              </button>
            </div>
          )}

          {loadOlderFailed && (
            <p className="text-caption text-status-error" data-testid="conversation-search-load-older-error">
              {t('conversationSearch.loadOlderFailed')}
            </p>
          )}

          {!outcome.queryTooShort && (
            <>
              {total === 0 ? (
                <p className="text-caption text-muted-foreground" data-testid="conversation-search-no-results">
                  {t('conversationSearch.noResults')}
                </p>
              ) : (
                <ul className="flex flex-col gap-0.5 max-h-64 overflow-y-auto" data-testid="conversation-search-results">
                  {visibleMatches.map((m, i) => {
                    const parts = highlightedSnippet(m);
                    return (
                      <li key={m.id}>
                        <button
                          type="button"
                          onClick={() => selectIndex(i)}
                          data-testid="conversation-search-result"
                          data-active={i === activeIndex}
                          className={`w-full text-left rounded px-2 py-1 text-caption transition-colors duration-motion-instant ${
                            i === activeIndex
                              ? 'bg-accent text-accent-foreground'
                              : 'hover:bg-accent/50 text-foreground'
                          }`}
                        >
                          <span className="text-muted-foreground/70">
                            {m.type === 'user' ? t('conversationSearch.authorYou') : agentName}
                            {' · '}
                            {formatRelativeTime(m.timestamp)}
                          </span>
                          <span className="block truncate">
                            {parts.before}
                            {parts.hit && <mark className="bg-transparent text-action font-medium">{parts.hit}</mark>}
                            {parts.after}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                  {hiddenCount > 0 && (
                    <li
                      className="text-caption text-muted-foreground/60 px-2 py-1"
                      data-testid="conversation-search-more"
                    >
                      {t('conversationSearch.moreResults', { count: hiddenCount })}
                    </li>
                  )}
                </ul>
              )}
            </>
          )}

          <p className="text-caption text-muted-foreground/60" data-testid="conversation-search-fleet-wide">
            {t('conversationSearch.fleetWidePending')}
          </p>
        </>
      )}
    </div>
  );
}
